/**
 * Splash.
 *
 * The client's logo — the drawing with "Meal4ALL®" lettered under it — over
 * the green field, then the tagline, set here in the shared type scale so it
 * follows the language. The logo carries the name, so the name is not set a
 * second time beneath it.
 *
 * This is a React component rather than a native launch image, so the same
 * splash appears on iOS, Android and the web. The native launch background and
 * the browser's pre-mount background are both painted the same green, so the
 * sequence is one continuous colour from tap to first screen.
 */

import { useEffect, useRef } from 'react';
import { Animated, Image, StyleSheet, View, useWindowDimensions } from 'react-native';

import { brandFixed, colors, radius, sizing, space } from '../../theme';
import { TAGLINE, unbrokenPhrases } from '../../components/common/Logo';
import { Text } from '../../components/common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

const LOGO = require('../../../assets/brand/splash-logo.png');
/** The logo's own proportions (assets/brand/splash-logo.png). */
const LOGO_ASPECT = 720 / 594;

export function SplashScreen() {
  const t = useT();
  const { width } = useWindowDimensions();
  const logoWidth = Math.min(width * 0.72, sizing.splash.logo);
  const fade = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fade, {
      toValue: 1,
      duration: 420,
      useNativeDriver: true,
    }).start();
  }, [fade]);

  return (
    <View style={styles.root} testID="screen-splash">
      <Animated.View style={[styles.lockup, { opacity: fade }]}>
        <Image
          source={LOGO}
          style={{ width: logoWidth, height: logoWidth / LOGO_ASPECT }}
          resizeMode="contain"
          accessibilityIgnoresInvertColors
          accessible
          accessibilityLabel="Meal4ALL"
          testID="splash-logo"
        />
        <View style={styles.rule}>
          <View style={styles.ruleLine} />
          <View style={styles.ruleDiamond} />
          <View style={styles.ruleLine} />
        </View>
        <Text variant="body" tone="onBrandMuted" align="center">{unbrokenPhrases(t(TAGLINE))}</Text>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: space.xl,
    backgroundColor: colors.bg.brand,
  },
  lockup: {
    alignItems: 'center',
  },
  rule: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    marginVertical: space.md,
  },
  ruleLine: {
    width: sizing.splash.rule,
    height: StyleSheet.hairlineWidth * 2,
    backgroundColor: colors.border.onBrand,
  },
  // The artwork sets a small heart between the two rules. React Native has no
  // path primitive and a bitmap for a 10pt ornament is not worth an asset, so
  // this is a diamond in the same brand orange.
  ruleDiamond: {
    width: sizing.splash.ornament,
    height: sizing.splash.ornament,
    borderRadius: radius.none,
    transform: [{ rotate: '45deg' }],
    backgroundColor: brandFixed.logoOrange,
  },
});
